import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  XAxis,
  YAxis,
  Area,
  ComposedChart,
  TooltipProps,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/Chart";
import {
  defaultChartConfig,
  chartMargins,
  defaultXAxisConfig,
  defaultYAxisConfig,
} from "./config";
import {
  TransformedChartData,
  getMonthFromDateString,
  isMonthBoundary,
  shouldShowLabelProgressive,
  formatXAxisDateProgressive,
} from "./utils";
import {
  DataInterval,
  GradientConfig,
  TooltipData,
  LineChartColors,
  XAxisLabelStrategy,
} from "./types";

export interface LineChartCoreProps {
  data: TransformedChartData[];
  interval: DataInterval;
  yAxisDomain: [number, number];
  hasComparisonData: boolean;
  className?: string;
  gradient?: GradientConfig | null;
  tooltipContent?: (data: TooltipData) => React.ReactNode;
  colors?: LineChartColors;
  xAxisLabelStrategy?: XAxisLabelStrategy;
}

const DATA_KEY = "current";

// Monthly system: Custom tick component that only shows labels at month boundaries
interface CustomXAxisTickMonthlyProps {
  x?: number;
  y?: number;
  payload?: { value: string };
}

const CustomXAxisTickMonthly: React.FC<
  CustomXAxisTickMonthlyProps & { allData: TransformedChartData[] }
> = ({ x = 0, y = 0, payload, allData }) => {
  if (!payload?.value) return null;

  // Only render if this is a month boundary
  if (!isMonthBoundary(payload.value, allData)) {
    return null;
  }

  const monthLabel = getMonthFromDateString(payload.value);
  if (!monthLabel) return null;

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={8} textAnchor="middle" fill="#666" fontSize={12}>
        {monthLabel}
      </text>
    </g>
  );
};

// Progressive system: Custom tick component using progressive labeling
interface CustomXAxisTickProgressiveProps {
  x?: number;
  y?: number;
  payload?: { value: string };
}

const CustomXAxisTickProgressive: React.FC<
  CustomXAxisTickProgressiveProps & { allData: TransformedChartData[] }
> = ({ x = 0, y = 0, payload, allData }) => {
  if (!payload?.value) return null;

  const shouldShow = shouldShowLabelProgressive(payload.value, allData);

  if (!shouldShow) {
    return null;
  }

  const allDates = allData.map((item) => item.period);
  const labelText = formatXAxisDateProgressive(payload.value, allDates);
  if (!labelText) return null;

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={8} textAnchor="middle" fill="#666" fontSize={12}>
        {labelText}
      </text>
    </g>
  );
};

export const LineChartCore: React.FC<LineChartCoreProps> = ({
  data,
  // We're keeping the interval for future use, disable lint check for now.
  interval, // eslint-disable-line @typescript-eslint/no-unused-vars
  yAxisDomain,
  hasComparisonData,
  className,
  gradient,
  tooltipContent,
  colors,
  xAxisLabelStrategy = "monthly",
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);

  const currentColor = colors?.current || defaultChartConfig.current.color;
  const comparisonColor =
    colors?.comparison || defaultChartConfig.comparison.color;

  // Generate unique gradient ID to avoid conflicts
  const gradientId = `chartGradient-${Math.random().toString(36).slice(2, 11)}`;

  // Determine if we should show dots (only when there's a single data point with original data)
  const realDataPoints = data.filter((point) => point.originalIndex >= 0);
  const shouldShowDots = realDataPoints.length === 1;

  // Format Y-axis values based on the system being used
  const formatYAxisTick = (value: number) => {
    if (xAxisLabelStrategy === "progressive") {
      return Math.round(value).toString();
    } else {
      return value.toFixed(1);
    }
  };

  // Choose the appropriate tick component based on strategy
  const TickComponent =
    xAxisLabelStrategy === "progressive"
      ? CustomXAxisTickProgressive
      : CustomXAxisTickMonthly;

  return (
    <div className="relative">
      <ChartContainer
        config={defaultChartConfig}
        className={className}
        data-testid="chart-container"
        ref={chartRef}
      >
        <ComposedChart
          accessibilityLayer
          data={data}
          margin={chartMargins}
          data-testid="line-chart"
        >
          {/* Gradient definition */}
          <defs>
            {gradient && (
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={gradient.startColor}
                  stopOpacity={gradient.startOpacity ?? 0.8}
                />
                <stop
                  offset="95%"
                  stopColor={gradient.endColor}
                  stopOpacity={gradient.endOpacity ?? 0}
                />
              </linearGradient>
            )}
          </defs>

          <CartesianGrid
            vertical={false}
            horizontal={true}
            data-testid="cartesian-grid"
          />
          <XAxis
            dataKey="period"
            {...defaultXAxisConfig}
            tick={<TickComponent allData={data} />}
            interval={0}
            allowDuplicatedCategory={false}
            data-testid="x-axis"
          />
          <YAxis
            {...defaultYAxisConfig}
            domain={yAxisDomain}
            tickCount={6}
            tickFormatter={formatYAxisTick}
            data-testid="y-axis"
          />

          {/* Tooltip */}
          <ChartTooltip
            cursor={false}
            content={
              tooltipContent ? (
                ({ active, payload, label }: TooltipProps<number, string>) => {
                  if (!active || !payload || payload.length === 0) return null;

                  // Filter out gradient-fill items
                  const filteredPayload = payload.filter(
                    (item) => item.name !== "gradient-fill",
                  );
                  const currentItem = filteredPayload.find(
                    (item) => item.dataKey === "current",
                  );

                  if (!currentItem) return null;

                  const comparisonItem = filteredPayload.find(
                    (item) => item.dataKey === "comparison",
                  );

                  const tooltipData: TooltipData = {
                    label: label || "",
                    currentValue: (currentItem.value as number) || 0,
                    comparisonValue: comparisonItem?.value as number,
                    isPartial:
                      (currentItem.payload?.isPartial as boolean) || false,
                    originalData:
                      (currentItem.payload as Record<string, unknown>) || {},
                  };

                  return (
                    <div className="rounded border bg-popover p-3 text-popover-foreground shadow-lg">
                      {tooltipContent(tooltipData)}
                    </div>
                  );
                }
              ) : (
                <ChartTooltipContent hideLabel hideKeys={["gradient-fill"]} />
              )
            }
            data-testid="chart-tooltip"
          />

          {/* Area fill for gradient */}
          {gradient && (
            <Area
              dataKey={DATA_KEY}
              stroke="none"
              fill={`url(#${gradientId})`}
              fillOpacity={1}
              type="linear"
              connectNulls={false}
              name="gradient-fill"
              data-testid="area-gradient"
            />
          )}

          {/* Current data line */}
          <Line
            dataKey={DATA_KEY}
            type="linear"
            name={t("chart.line.current", "Current")}
            stroke={currentColor}
            strokeWidth={2}
            dot={shouldShowDots}
            activeDot={{
              r: 6,
              fill: currentColor,
              stroke: "var(--background)",
              strokeWidth: 4,
            }}
            connectNulls={false}
            data-testid="line-current"
          />

          {/* Comparison line */}
          {hasComparisonData && (
            <Line
              dataKey="comparison"
              type="linear"
              name={t("chart.line.previousYear", "Previous Year")}
              stroke={comparisonColor}
              strokeWidth={2}
              connectNulls={false}
              strokeDasharray="6 4"
              dot={shouldShowDots}
              data-testid="line-comparison"
            />
          )}
        </ComposedChart>
      </ChartContainer>
    </div>
  );
};
